import { useLazyQuery } from '@apollo/client';
import { faExclamation } from '@fortawesome/free-solid-svg-icons/faExclamation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Container, FlexGrid, Header, Image, Loader, Message, Panel, Text } from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import gql from 'graphql-tag';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { isWindowAvailable, scrollToTop } from '../../../lib/utils';
import BRANDS from '../../constants/brands';
import { Banner } from '../Banner';
import GenericStationList from '../GenericStationList';
import HeroAppDownload from '../HeroAppDownload';
import MiniBrandList from '../MiniBrandList';
import Page from '../Page';
import SimpleHelmet from '../SimpleHelmet';
import styles from './BrandPage.module.css';

const cx = classnames.bind(styles);

export const GET_STATIONS = gql`
  query GetStationsByBrand($brandId: Int!, $lat: Float, $lng: Float, $lang: String) {
    stations(brandId: $brandId, lat: $lat, lng: $lng) {
      results {
        address {
          country
          line1
          line2
          locality
          postalCode
          region
        }
        badges(lang: $lang) {
          badgeId,
          callToAction,
          campaignId,
          clickTrackingUrl,
          description,
          detailsImageUrl,
          detailsImpressionTrackingUrls,
          imageUrl,
          impressionTrackingUrls,
          targetUrl,
          title,
        }
        brands {
          brandId
          brandingType
          imageUrl
          name
        }
        distance
        emergencyStatus {
          hasDiesel {
            nickname
            reportStatus
            updateDate
          }
          hasGas {
            nickname
            reportStatus
            updateDate
          }
          hasPower {
            nickname
            reportStatus
            updateDate
          }
        }
        enterprise
        fuels
        hasActiveOutage
        id
        latitude
        longitude
        name
        prices {
          cash {
            formattedPrice
            nickname
            postedTime
            price
          }
          credit {
            formattedPrice
            nickname
            postedTime
            price
          }
          discount
          fuelProduct
        }
        priceUnit
        ratingsCount
        starRating
        isFuelmanSite
      }
    }
    walletStatus {
      accountStatus
      hasPwGBCard
      hasPwGBPlusCard
    }
  }
`;

export default function BrandPage() {
  const { t, i18n } = useTranslation(['brand', 'location', 'station']);
  const { brandKey } = useParams();
  const brand = useMemo(() => BRANDS.find(({ uri }) => uri.toLowerCase() === brandKey.toLowerCase()), [brandKey]);

  const [hasFetchedStations, setHasFetchedStations] = useState(false);
  const [fetchStations, {
    data: stationsResponse,
    loading: isFetchingStations,
    error: errorFetchingStations,
  }] = useLazyQuery(GET_STATIONS, {
    onCompleted: () => {
      setHasFetchedStations(true);
    },
  });
  const stations = stationsResponse?.stations?.results;
  const { hasPwGBCard, hasPwGBPlusCard, accountStatus: payAccountStatus } = stationsResponse?.walletStatus || {};

  const brandDisplay = brand?.name ? ` ${brand.name}` : '';
  const brandTitle = t('brandPage.findBrandStations', { ns: 'brand', brandName: brandDisplay });
  const brandDescription = t('brandPage.findCheapPrices', { ns: 'brand', brandName: brand?.name });
  const pageMeta = useMemo(() => ({ Brand: brandKey }), [brandKey]);

  const [locationError, setLocationError] = useState(undefined);

  useEffect(() => {
    if (isWindowAvailable() && 'geolocation' in window.navigator) {
      window.navigator.geolocation.getCurrentPosition((position) => {
        fetchStations({
          variables: {
            brandId: brand.brandId,
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            lang: i18n.language,
          },
        });
      }, (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          setLocationError(error.message);
        }
      });
    }
  }, [brand.brandId, fetchStations, i18n.language]);

  useEffect(() => {
    scrollToTop();
  }, [brandKey]);

  let stationContent;
  if (isFetchingStations) {
    stationContent = <Loader style={{ margin: '50px auto' }} />;
  } else if (errorFetchingStations || (stations === undefined && hasFetchedStations)) {
    stationContent = (
      <Message color="red" icon={<FontAwesomeIcon size="xs" icon={faExclamation} />}>
        {t('errorFetchingStations', { ns: 'station' })}
      </Message>
    );
  } else if (locationError) {
    stationContent = (
      <Message
        color="red"
        icon={<FontAwesomeIcon size="xs" icon={faExclamation} />}
      >
        {t('userDeniedLocationPermissions', { ns: 'location' })}
      </Message>
    );
  } else if (!hasFetchedStations) {
    stationContent = (
      <Message color="gold">
        {t('brandPage.waitForGeolocation', { ns: 'brand' })}
      </Message>
    );
  } else if (stations.length === 0) {
    stationContent = (
      <Message color="white">
        {t('brandPage.noStations', { ns: 'brand' })}
      </Message>
    );
  } else {
    stationContent = (
      <GenericStationList
        stations={stations}
        hasPwGBCard={hasPwGBCard}
        hasPwGBPlusCard={hasPwGBPlusCard}
        payAccountStatus={payAccountStatus}
      />
    );
  }

  return (
    <Page
      name="Brand_Page"
      meta={pageMeta}
      compact
      title={brandTitle}
      description={brandDescription}
    >
      {brand?.name && (
        <SimpleHelmet
          image={brand?.imageUrl}
          title={brandTitle}
          description={brandDescription}
        />
      )}
      {/* Mini-Hero */}
      <Banner
        backgroundImage="//static.gasbuddy.com/web/consumer/Mini-hero-20.jpg"
        height={186}
        className={cx('bannerContainer')}
        darker
        parallax
      >
        <div className={cx('brandHeaderContainer')}>
          <Image
            className={cx('brandImage')}
            src={brand?.imageUrl}
            alt={t('brandPage.logoImageAlt', { ns: 'brand', brandName: brand?.name })}
          />
          <Header as="h1" color="white" className={cx('brandDisplayName')}>
            {brand?.name}
          </Header>
        </div>
      </Banner>

      <Container>
        <FlexGrid container padded>
          <FlexGrid.Column tablet={7} mobile={12}>
            <Header as="h2" color="midnight" className={cx('mainHeader', 'headerAdditions')}>
              {t('brandPage.nearYou', { ns: 'brand', brandName: brand?.name })}
            </Header>
            {stationContent}
          </FlexGrid.Column>

          <FlexGrid.Column tablet={5} mobile={12}>
            <Header as="h2" className={cx('headerAdditions')}>
              {t('brandPage.brandInformation', { ns: 'brand' })}
            </Header>
            <Panel color="white" bordered rounded>
              <Text as="p">
                {t(`descriptions.${brand.uri}`, { ns: 'brand' })}
              </Text>
            </Panel>
            <br />
            <Header as="h2" className={cx('headerAdditions')}>
              {t('brandPage.findCheapGasByBrand', { ns: 'brand' })}
            </Header>
            <MiniBrandList activeBrand={brand} />
          </FlexGrid.Column>
        </FlexGrid>
      </Container>

      <HeroAppDownload />
    </Page>
  );
}
